import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Steps,
  message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { updateUserSettings } from '../../services/api';
import { captureEvent } from '../../services/analytics';
import { ApiErrorCode, getApiErrorCode, getApiErrorMessage } from '../../utils/errorUtils';
import { AutoApplyGateVariant } from './AutoApplyGmailGateModal';
import {
  AutoApplyExclusionSuggestion,
  AutoApplyThreshold,
  ExcludedContact,
  UserSettings,
} from '../../types';
import css from './AutoApplyActivationModal.module.scss';

export const AUTO_APPLY_THRESHOLD_LABELS: Record<AutoApplyThreshold, string> = {
  perfect_fit: 'Perfect Fit only',
  strong_alignment: 'Strong Alignment and up',
};

// What a FIRST activation starts on, and the label that carries "(recommended)".
// Perfect Fit reads as the safe choice and is really the silent one: in August
// 2026 it was 69 of 939 live pending opportunities, and a third of users with
// active searches had none at all while holding dozens of strong-alignment
// matches — so the narrow default did not send them fewer applications, it sent
// them none. Blast radius is bounded by the daily cap and the exclusion lists,
// both unchanged; Potential Candidate stays out either way.
//
// Paired with the nudge sweep's pool query (users/tasks.py), which must admit
// the same ratings or it goes silent for the users this serves.
const RECOMMENDED_THRESHOLD: AutoApplyThreshold = 'strong_alignment';

const MAX_EXCLUSIONS = 50;
// Start showing the "N/50" indicator once a list gets close to the cap.
const NEAR_CAP = 40;

// Ties the resume explainer to the companies field. Every tag rc-select renders
// is an aria-hidden span, so a pre-filled field announces itself as "blank" —
// true for a list the user typed, a lie for one that arrived holding their
// employer. This is the only thing that reads the sentence out loud.
const RESUME_HINT_ID = 'auto-apply-resume-suggestions-hint';

// Describes the control, not the companies — and so is true at every moment.
// `suggestionsAtOpen` is frozen at open, which is right for the acknowledgement
// and wrong for a promise: "Auto-Apply will skip them" outlives the tags, so it
// keeps guaranteeing a skip for an employer the user has just deliberately
// removed. That is false reassurance about the one exposure this feature
// exists to prevent. The tags below are already the live list, so pointing at
// them says more, and stays honest through every edit. Invariant text also
// retires the singular/plural split, and with it a whole class of grammar bug.
const SKIP_LINE =
  "Auto-Apply skips every company listed below. Remove any you'd still like to apply to.";

// Names every employer the server flagged, never just the first. `is_current`
// is computed per entry (`end is None`) and the merge above it dedupes on the
// company name alone, so two open-ended roles — a board seat beside a day job,
// or an end date the parser lost — arrive as two current entries. They rank
// first, so a lead-only sentence would bury the second inside "and N others":
// exactly the employer this screen exists to keep.
const currentEmployerClause = (currents: AutoApplyExclusionSuggestion[]): string => {
  const names = currents.map((s) => s.company);
  if (names.length === 1) return `${names[0]} looks like where you work now`;
  const last = names[names.length - 1];
  return `${names.slice(0, -1).join(', ')} and ${last} look like places you work now`;
};

// 422s come back with a `loc` naming the field; the raw msg alone doesn't.
// Map known fields to the labels the user actually sees in this modal.
const FIELD_LABELS: Record<string, string> = {
  autoApplyThreshold: 'Match level',
  autoApplyDailyCap: 'Daily cap',
  autoApplyExcludedCompanies: 'Excluded companies',
  autoApplyExcludedContacts: 'Excluded contacts',
  autoApplyEnabled: 'Auto-Apply',
};

function mapSaveError(error: any): string {
  const detail = error?.response?.data?.detail;
  if (error?.response?.status === 422 && Array.isArray(detail) && detail.length) {
    const first = detail[0];
    const loc = Array.isArray(first?.loc) ? first.loc : [];
    const label = FIELD_LABELS[String(loc[loc.length - 1])];
    if (label && first?.msg) return `${label}: ${first.msg}`;
  }
  // getApiErrorMessage already renders 422s as "field: msg" as a fallback.
  return getApiErrorMessage(error);
}

interface ContactRow {
  first: string;
  last: string;
}

// Mirror the backend's cleaning: trim, then drop rows missing either name.
const cleanContacts = (rows: ContactRow[]): ExcludedContact[] =>
  rows
    .map((row) => ({ first: row.first.trim(), last: row.last.trim() }))
    .filter((row) => row.first && row.last);

const cleanCompanies = (names: string[]): string[] =>
  names.map((name) => name.trim()).filter(Boolean);

// Stored entries first — the user's own list stays where they left it — then
// suggestions in server order, which leads with the current employer. The
// server already subtracts stored entries, with a normalizer more aggressive
// than this one (casefold, punctuation, legal suffixes), so this dedupe should
// never fire — it guards the seed against a payload that breaks that contract.
// Returns the seeded list AND the suggestions that survived into it, because
// the two can differ and only the survivors were reviewable.
const mergeCompanies = (
  stored: string[],
  suggestions: AutoApplyExclusionSuggestion[]
): { merged: string[]; seeded: AutoApplyExclusionSuggestion[] } => {
  // Coerced, not trusted, on BOTH inputs. Stored exclusions live in an untyped
  // JSONB column that the backend itself still guards with isinstance(c, str),
  // and a non-string in either list throws inside the open effect, taking the
  // whole modal down over one bad entry.
  const storedNames = stored.map((name) => String(name ?? ''));
  const seen = new Set(storedNames.map((name) => name.trim().toLowerCase()));
  const merged = [...storedNames];
  const accepted: AutoApplyExclusionSuggestion[] = [];
  for (const suggestion of suggestions) {
    // Null/undefined and anything blank after trimming drop out at the guard
    // below — but String(12345) is truthy and sails through it, so the COERCED
    // value is what gets seeded, on both sides: a raw number in `merged` makes
    // cleanCompanies throw on every render, and one in `accepted` throws again
    // in the counts.
    const company = String(suggestion.company ?? '');
    const key = company.trim().toLowerCase();
    if (key && !seen.has(key)) {
      seen.add(key);
      merged.push(company);
      accepted.push({ ...suggestion, company });
    }
  }
  // The server capped suggestions at 50 − stored, counting stored the way the
  // PUT validator does (trimmed, blanks dropped); this counts raw stored, so
  // the two disagree by the number of blanks that ever slipped past that
  // validator. Stored leads either way, so the cap can only drop suggestions,
  // never the user's entries — and `seeded` must report what is left after it,
  // not what arrived. A blank-padded stored list can starve the seed entirely,
  // and acknowledging a review of chips that never rendered is irreversible.
  const capacity = Math.max(0, MAX_EXCLUSIONS - stored.length);
  return {
    merged: merged.slice(0, MAX_EXCLUSIONS),
    seeded: accepted.slice(0, capacity),
  };
};

// The cap the modal actually uses: cleared → default 5, else clamped to 1-10.
const clampCap = (value: number | null): number =>
  Math.min(10, Math.max(1, Math.round(value ?? 5)));

export interface AutoApplyActivationModalProps {
  open: boolean;
  onClose: () => void;
  // Seeds every field, and save PUTs all five of them — so the caller must
  // hold the user's real settings before it may render this modal. Not
  // nullable by design: a null seed would save empty exclusion lists.
  settings: UserSettings;
  onSaved: (settings: UserSettings) => void;
  /**
   * The save was refused because Gmail cannot send (409). The host reopens its
   * gate at this variant. This modal deliberately stays MOUNTED underneath, so
   * dismissing the gate returns the user to the threshold, cap and exclusion
   * list they just filled in rather than to an empty card.
   *
   * That is the limit of it: proceeding to Google is a full-page redirect, and
   * the draft does not survive it. On return the modal is rebuilt from stored
   * settings. Accepted rather than solved — this path is only reachable when the
   * pre-form gate was skipped (a failed status probe, or a disconnect in another
   * tab), so the ordinary route never fills in a form it cannot save.
   */
  onGmailRequired?: (variant: AutoApplyGateVariant) => void;
}

/**
 * Two-step Auto-Apply activation (design §10). Step 1 explains what will be
 * sent and collects threshold + daily cap; step 2 is THE GATE — the design
 * mandates an active opt-out: Activate stays disabled until the user either
 * adds ≥1 exclusion (company or contact) or explicitly ticks "no exclusions
 * needed". Pre-filled resume suggestions satisfy it on arrival — the safe
 * default is already applied; clear them all and the gate re-engages.
 * Self-contained so the H8 offer card can open it from anywhere.
 */
const AutoApplyActivationModal: React.FC<AutoApplyActivationModalProps> = ({
  open,
  onClose,
  settings,
  onSaved,
  onGmailRequired,
}) => {
  const manageMode = settings.autoApplyEnabled === true;

  const [step, setStep] = useState(0);
  const [threshold, setThreshold] = useState<AutoApplyThreshold>(RECOMMENDED_THRESHOLD);
  // null while the user has the field cleared mid-edit; coerced on blur/save.
  const [cap, setCap] = useState<number | null>(5);
  const [companies, setCompanies] = useState<string[]>([]);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [noExclusions, setNoExclusions] = useState(false);
  // Frozen on open: what the user was actually shown. Deriving this from the
  // `settings` prop at save time would let a parent refetch change the
  // acknowledgement we send for a review that already happened on screen.
  const [suggestionsAtOpen, setSuggestionsAtOpen] = useState<
    AutoApplyExclusionSuggestion[]
  >([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed from current settings each time the modal opens (Manage prefill).
  // Seed ONLY on the closed→open transition: a parent settings refetch while
  // the modal is open (new prop identity) must not clobber in-progress edits.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (!open || wasOpen) return;
    setStep(0);
    // ?? guards older payloads that predate a field, never an absent settings
    // object — the props type makes that unrepresentable.
    // Manage keeps whatever the user already chose; only a FIRST activation gets
    // the recommendation. Existing users are never re-pointed at a wider setting
    // behind their back — that would silently change what goes out from their
    // own Gmail.
    setThreshold(
      manageMode ? settings.autoApplyThreshold ?? RECOMMENDED_THRESHOLD : RECOMMENDED_THRESHOLD
    );
    setCap(settings.autoApplyDailyCap ?? 5);
    const { merged, seeded } = mergeCompanies(
      settings.autoApplyExcludedCompanies ?? [],
      settings.autoApplyExclusionSuggestions ?? []
    );
    setCompanies(merged);
    setSuggestionsAtOpen(seeded);
    setContacts(
      (settings.autoApplyExcludedContacts ?? []).map((c) => ({ first: c.first, last: c.last }))
    );
    setNoExclusions(false);
    setError(null);
    // `manageMode` is read above and deliberately not listed. This is a
    // seed-on-open effect: `prevOpenRef` makes every run whose `open` was
    // already true return before the first setState, so the only run that
    // reaches the read is the one from the commit where `open` flipped false →
    // true, and that commit's `manageMode` is the current one. Listing it would
    // add runs that provably do nothing and would advertise a re-seed on a
    // mid-open toggle that the guard makes impossible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, settings]);

  const cleanedCompanies = useMemo(() => cleanCompanies(companies), [companies]);
  const cleanedContacts = useMemo(() => cleanContacts(contacts), [contacts]);
  const exclusionCount = cleanedCompanies.length + cleanedContacts.length;
  const gateSatisfied = exclusionCount >= 1 || noExclusions;
  // The authorization decision, and a one-way door: the marker it stamps is
  // never cleared, so a user stamped without seeing suggestions loses them
  // permanently. THE invariant for everything derived from suggestions, copy
  // and counts alike: read `suggestionsAtOpen` — what rendered at open — never
  // the payload, which the cap can trim before a chip reaches the screen.
  const hasSuggestions = suggestionsAtOpen.length > 0;
  // Filtered rather than sliced off the front: the server ranks currents first,
  // but nothing in this component should depend on that ordering holding.
  const currentSuggestions = useMemo(
    () => suggestionsAtOpen.filter((s) => s.isCurrent),
    [suggestionsAtOpen]
  );
  // What the save event reports (invariant above). Matching is case-insensitive
  // but exact, unlike the server's normalizer, so a user who retypes a name
  // reads as having dropped it — a lower bound, the safe direction for a
  // safety metric. The suggestion side is trimmed because the payload is
  // untrusted, not because the server pads it.
  const suggestionCounts = useMemo(() => {
    const keptNames = new Set(cleanedCompanies.map((name) => name.toLowerCase()));
    const survived = (s: AutoApplyExclusionSuggestion) =>
      keptNames.has(s.company.trim().toLowerCase());
    return {
      shown: suggestionsAtOpen.length,
      kept: suggestionsAtOpen.filter(survived).length,
      // Broken out because the totals cannot tell a pruned 2015 job from a
      // pruned current employer, and only the second is the failure this
      // feature exists to prevent.
      currentShown: currentSuggestions.length,
      currentKept: currentSuggestions.filter(survived).length,
    };
  }, [cleanedCompanies, currentSuggestions, suggestionsAtOpen]);
  const otherCount = suggestionsAtOpen.length - currentSuggestions.length;
  // The fallback branch names the first suggestion itself rather than every
  // current one, so its count covers whatever follows that single name.
  const restCount = suggestionsAtOpen.length - 1;
  const clampedCap = clampCap(cap);
  const thresholdLabel = AUTO_APPLY_THRESHOLD_LABELS[threshold];

  const handleCompaniesChange = (values: string[]) => {
    setCompanies(values.slice(0, MAX_EXCLUSIONS));
  };

  const updateContact = (index: number, patch: Partial<ContactRow>) => {
    setContacts((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeContact = (index: number) => {
    setContacts((rows) => rows.filter((_, i) => i !== index));
  };

  const addContact = () => {
    setContacts((rows) =>
      rows.length >= MAX_EXCLUSIONS ? rows : [...rows, { first: '', last: '' }]
    );
  };

  const handleSave = async () => {
    if (!gateSatisfied || saving) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateUserSettings({
        autoApplyEnabled: true,
        autoApplyThreshold: threshold,
        autoApplyDailyCap: clampedCap,
        autoApplyExcludedCompanies: cleanedCompanies,
        autoApplyExcludedContacts: cleanedContacts,
        // Spreading `false` is a no-op, so a save that reviewed nothing omits
        // the key outright rather than sending it as `false`.
        ...(hasSuggestions && { autoApplyExclusionSuggestionsReviewed: true }),
      });
      message.success(
        manageMode
          ? 'Auto-Apply settings saved.'
          : `Auto-Apply is on — up to ${updated.autoApplyDailyCap}/day.`
      );
      captureEvent(manageMode ? 'auto_apply_settings_saved' : 'auto_apply_activated', {
        threshold,
        daily_cap: clampedCap,
        excluded_companies: cleanedCompanies.length,
        excluded_contacts: cleanedContacts.length,
        // Read these across BOTH event names, filtered on shown > 0. Every
        // user who had Auto-Apply on before this shipped keeps a NULL marker,
        // so the server offers them suggestions too and Manage is their only
        // route to the review — that cohort is the largest, is already
        // auto-sending against a list that never held their employer, and
        // reports only under `auto_apply_settings_saved`. Filtering to
        // `auto_apply_activated` drops exactly them, and nothing can rebuild
        // it: the marker is one-way and the server keeps no record of what it
        // offered.
        suggestions_shown: suggestionCounts.shown,
        suggestions_kept: suggestionCounts.kept,
        suggestions_current_shown: suggestionCounts.currentShown,
        suggestions_current_kept: suggestionCounts.currentKept,
        // Only truthful when the opt-out actually applied — the checkbox can be
        // ticked and then hidden by entries added afterwards (stale flag).
        no_exclusions: exclusionCount === 0 && noExclusions,
      });
      onSaved(updated);
      onClose();
    } catch (err) {
      // Gmail refused before anything was written. Hand the whole condition to
      // the host's gate — which offers the fix — instead of stranding an error
      // banner inside a modal whose primary button cannot succeed.
      const code = getApiErrorCode(err);
      if (
        onGmailRequired &&
        (code === ApiErrorCode.GmailNotConnected ||
          code === ApiErrorCode.GmailSendScopeMissing)
      ) {
        setError(null);
        onGmailRequired(code === ApiErrorCode.GmailSendScopeMissing ? 'fix' : 'connect');
        return;
      }
      setError(mapSaveError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        <div className={css.modalTitle}>
          <ThunderboltOutlined />
          <span>{manageMode ? 'Manage Auto-Apply' : 'Set up Auto-Apply'}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(560px, 94vw)"
      className={css.modal}
    >
      <Steps
        size="small"
        current={step}
        className={css.steps}
        items={[{ title: 'How it works' }, { title: 'Exclusions' }]}
      />

      {step === 0 && (
        <div className={css.stepBody}>
          <p className={css.explainer}>
            We'll email your tailored application directly to the selected contact, at most{' '}
            {clampedCap} a day, only for {thresholdLabel} matches. You'll get a daily summary of
            everything we sent.
          </p>

          <div className={css.field}>
            <span className={css.fieldLabel} id="auto-apply-threshold-label">
              Send only for
            </span>
            <Radio.Group
              aria-labelledby="auto-apply-threshold-label"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className={css.thresholdGroup}
            >
              <Radio value="strong_alignment">Strong Alignment and up (recommended)</Radio>
              <Radio value="perfect_fit">Perfect Fit only</Radio>
            </Radio.Group>
          </div>

          <div className={css.field}>
            <label className={css.fieldLabel} htmlFor="auto-apply-daily-cap">
              Daily cap
            </label>
            <div className={css.capRow}>
              <InputNumber
                id="auto-apply-daily-cap"
                min={1}
                max={10}
                precision={0}
                value={cap}
                onChange={(value) => setCap(value)}
                onBlur={() => setCap(clampCap(cap))}
              />
              <span className={css.capHint}>applications per day (1–10)</span>
            </div>
          </div>

          <div className={css.footerButtons}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" onClick={() => setStep(1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className={css.stepBody}>
          {hasSuggestions ? (
            // Once their employers are already in the box below, telling the
            // user to add them reads as a bug. Naming them here does the job
            // the rejected per-tag "Appears current" badge would have done,
            // without adding an element — so the bold run has to carry the
            // safety signal, not just the company name. "Looks like" hedges in
            // plain words: `isCurrent` is inferred from a missing end date, and
            // a parser that lost the date is indistinguishable from a job the
            // user still holds.
            <p className={css.explainer} id={RESUME_HINT_ID}>
              {/* Manage-mode only. An existing user opens this on a list they
                  curated themselves and finds companies they never added;
                  without attribution that reads as corrupted settings, and the
                  fix a user reaches for is deleting the unfamiliar entries —
                  which are the safety entries. A first-time user has no
                  curated list to be surprised by, so they are spared the line. */}
              {manageMode && (
                <>
                  We now suggest exclusions, so your list has entries you didn't add.
                  <br />
                </>
              )}
              {currentSuggestions.length > 0 ? (
                <>
                  <strong>{currentEmployerClause(currentSuggestions)}</strong>
                  {otherCount > 0
                    ? ` — added from your resume with ${otherCount} ${
                        otherCount === 1 ? 'other' : 'others'
                      }.`
                    : ' — added from your resume.'}
                </>
              ) : (
                <>
                  Added from your resume: <strong>{suggestionsAtOpen[0].company}</strong>
                  {restCount > 0
                    ? ` and ${restCount} ${restCount === 1 ? 'other' : 'others'}.`
                    : '.'}
                </>
              )}
              <br />
              {SKIP_LINE}
            </p>
          ) : (
            <p className={css.explainer}>
              Add your current employer, and anyone who shouldn't hear from you. We'll skip
              these when auto-applying.
            </p>
          )}

          <div className={css.field}>
            <label className={css.fieldLabel} htmlFor="auto-apply-excluded-companies">
              Companies
              {cleanedCompanies.length >= NEAR_CAP && (
                <span className={css.countHint}>
                  {cleanedCompanies.length}/{MAX_EXCLUSIONS}
                </span>
              )}
            </label>
            <Select
              id="auto-apply-excluded-companies"
              aria-describedby={hasSuggestions ? RESUME_HINT_ID : undefined}
              mode="tags"
              value={companies}
              onChange={handleCompaniesChange}
              placeholder="Company names…"
              tokenSeparators={[',']}
              notFoundContent={null}
              suffixIcon={null}
              className={css.companiesSelect}
            />
          </div>

          <div className={css.field}>
            <span className={css.fieldLabel}>
              People
              {cleanedContacts.length >= NEAR_CAP && (
                <span className={css.countHint}>
                  {cleanedContacts.length}/{MAX_EXCLUSIONS}
                </span>
              )}
            </span>
            {contacts.map((row, index) => (
              <div className={css.contactRow} key={index}>
                <Input
                  aria-label={`Excluded contact ${index + 1} first name`}
                  placeholder="First name"
                  value={row.first}
                  onChange={(e) => updateContact(index, { first: e.target.value })}
                />
                <Input
                  aria-label={`Excluded contact ${index + 1} last name`}
                  placeholder="Last name"
                  value={row.last}
                  onChange={(e) => updateContact(index, { last: e.target.value })}
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  aria-label={`Remove excluded contact ${index + 1}`}
                  onClick={() => removeContact(index)}
                />
              </div>
            ))}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addContact}
              disabled={contacts.length >= MAX_EXCLUSIONS}
              className={css.addContactButton}
            >
              Add a person
            </Button>
          </div>

          {exclusionCount === 0 && (
            <Checkbox
              checked={noExclusions}
              onChange={(e) => setNoExclusions(e.target.checked)}
              className={css.noExclusionsCheckbox}
            >
              I've thought about it — no exclusions needed
            </Checkbox>
          )}

          {error && (
            <Alert type="error" showIcon className={css.errorAlert} message={error} />
          )}

          <div className={css.footerButtons}>
            <Button onClick={() => setStep(0)} disabled={saving}>
              Back
            </Button>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={!gateSatisfied}
              loading={saving}
            >
              {manageMode ? 'Save changes' : 'Activate'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AutoApplyActivationModal;
